import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  tags?: string[];
  keywords?: string[];
}

/**
 * Sanitize text for meta tags: removes HTML, emojis, and limits length.
 */
const sanitizeMeta = (text: string = "", maxLength: number = 160): string => {
  if (!text) return "";
  // Remove HTML tags
  const noHtml = text.replace(/<[^>]*>?/gm, '');
  // Remove emojis and special symbols
  const noEmojis = noHtml.replace(
    /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDC00-\uDFFF])/g,
    ''
  );
  // Remove line breaks and extra spaces
  const clean = noEmojis.replace(/\s+/g, ' ').trim();
  // Truncate
  return clean.length > maxLength ? clean.slice(0, maxLength - 3) + "..." : clean;
};

export const SEO: React.FC<SEOProps> = ({
  title = "Speem - Speak on Steem",
  description = "Speem empowers Steem community to the next level, allowing video content creators to post their content on the Web 3 across the Steem Blockchain.",
  image = "https://speem.watch/assets/img/logo.webp",
  url = window.location.href,
  type = "website",
  author,
  publishedTime,
  tags = [],
  keywords = []
}) => {
  const sanitizedTitle = sanitizeMeta(title, 60);
  const sanitizedDescription = sanitizeMeta(description, 160);
  const siteName = "Speem";
  const twitterHandle = "@speemwatch";

  const normalizedTags: string[] = Array.isArray(tags)
    ? tags
    : (typeof tags === 'string' ? (tags as string).split(/[,\s]+/).filter(Boolean) : []);

  const normalizedKeywords: string[] = Array.isArray(keywords)
    ? keywords
    : (typeof keywords === 'string' ? (keywords as string).split(/[,\s]+/).filter(Boolean) : []);

  // Structured Data (JSON-LD)
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": type === 'article' ? 'Article' : (type === 'profile' ? 'Person' : 'WebSite'),
    "name": sanitizedTitle,
    "description": sanitizedDescription,
    "url": url,
    "image": image,
  };

  if (type === 'article') {
    Object.assign(jsonLd, {
      "headline": sanitizedTitle,
      "datePublished": publishedTime,
      "author": {
        "@type": "Person",
        "name": author || "Speem User",
      }
    });
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{sanitizedTitle}</title>
      <meta name="description" content={sanitizedDescription} />
      {normalizedKeywords.length > 0 && <meta name="keywords" content={normalizedKeywords.join(', ')} />}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={sanitizedTitle} />
      <meta property="og:description" content={sanitizedDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={sanitizedTitle} />
      <meta name="twitter:description" content={sanitizedDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content={twitterHandle} />
      {author && <meta name="twitter:creator" content={twitterHandle} />}

      {/* App Links (Mobile/Wallet Compatibility) */}
      <meta property="al:web:url" content={url} />
      {/* Assuming a schema for deep linking */}
      <meta property="al:ios:url" content={url.replace('https://speem.watch', 'speem://')} />
      <meta property="al:android:url" content={url.replace('https://speem.watch', 'speem://')} />

      {/* Article Specific Meta Tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {normalizedTags.map((tag, index) => (
        <meta key={`tag-${index}`} property="article:tag" content={tag} />
      ))}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
};
